/**
 * http://documentation.viafoura.com/javascript.php#reset
 * http://documentation.viafoura.com/javascript.php#events
 */

import { isBrowser } from "../environmentDetection/library";
import { getDomainHost } from "../path/library";
import { loadMembershipActionsLR } from "../membership";
import logger from "../logger/library";

const Viafoura = {
    /**
     * Add a callback to the Viafoura queue (a queue executed once Viafoura is in the 'ready' state)
     * @param {callback} cb The callback to be added to the queue
     */
    addOnReadyCallback: function (cb) {
        if (isBrowser()) {
            window.vfQ = window.vfQ || [];
            window.vfQ.push(cb);
        }
    },

    /**
     * Reset Viafoura https://documentation.viafoura.com/js-api-docs/index.html#vfcontext
     * On page navigation, Viafoura should be reset after page metadata is updated and
     * Viafoura widgets are added to the DOM.
     */
    reset: function () {
        if (this._isContextDefined()) {
            window.vf.context.reset();
        }
    },

    /**
     * Publishes the "tray open" event to Viafoura for the profile tab. If the user is not logged in,
     * Viafoura will trigger "authentication required", so the user is greeted with the login form
     * @param {object} event - Event object corresponding to this tray open
     */
    openProfile: function (event) {
        if (this._isViafouraLoaded()) {
            window.vf.$publish(
                "tray",
                "open",
                event.currentTarget,
                "vf-profile"
            );
        }
    },

    /**
     * Subscribe to whenever the Viafoura user is loaded/reloaded
     * @param {callback} cb The callback to be executed once the user info is fetched
     */
    subscribeUserInfo: function (cb) {
        if (this._isViafouraLoaded()) {
            window.vf.$subscribe("user", "loaded", (userContext) =>
                cb(userContext)
            );
        }
    },

    /**
     * Fetch the current Viafoura user context
     * @return {Promise<object>} The Viafoura user context object
     */
    getUserContext: async function () {
        try {
            if (!this._isContextDefined()) {
                const errorMsg = "Viafoura context undefined - not loaded";
                logger.warn(`(getUserContext) ${errorMsg}`);
                throw new Error(errorMsg);
            }

            const { validateAccessToken, getSessionToken } =
                await loadMembershipActionsLR();
            const userValidToken = await getSessionToken();

            // This condition is to exit early from checking validateAccessToken with null token
            // This is for the unAuth user(first time user) when token is not set
            if (!userValidToken) {
                return Promise.resolve({});
            }
            const isValid = await validateAccessToken(userValidToken);

            if (isValid) {
                return window.vf.context.get("user");
            } else {
                const errorMsg = "Invalid access token for Viafoura Context";
                throw new Error(errorMsg);
            }
        } catch (error) {
            const errorMsg = `Failed to fetch user context - ${error.message}`;
            return Promise.reject(errorMsg);
        }
    },

    /**
     * Determine if the provided Viafoura user context is authenticated
     * @param {object} userContext The Viafoura user context object
     * @return {boolean} True if the user is authenticated, else false
     */
    isUserAuthenticated: function (userContext) {
        return (
            !!userContext?.user_privilege &&
            userContext.user_privilege !== "guest"
        );
    },

    subscribeTrayOpen: function (cb) {
        if (this._isViafouraLoaded()) {
            window.vf.$subscribe("tray", "opened", (tabName) => cb(tabName));
        }
    },

    subscribeTrayClosed: function (cb) {
        if (this._isViafouraLoaded()) {
            window.vf.$subscribe("tray", "closed", (tabName) => cb(tabName));
        }
    },

    /**
     * Subscribe to whenever there is new notifications for the user
     * @param {callback} cb The callback to be executed once the notification info is received
     */
    subscribeNotifications: function (cb) {
        if (this._isViafouraLoaded()) {
            window.vf.$subscribe(
                "user-notifications",
                "new-count-changed",
                (notificationCount) => cb(notificationCount)
            );
        }
    },

    /**
     * unsubscribe user loading
     */
    unsubscribeNotifications: function () {
        if (this._isViafouraLoaded()) {
            window.vf.$unsubscribe("user-notifications", "new-count-changed");
            window.vf.$unsubscribe("commenting", "sorted");
            window.vf.$unsubscribe("user", "loaded");
            window.vf.$unsubscribe("tray", "opened");
            window.vf.$unsubscribe("tray", "closed");
        }
    },

    /**
     * unsubscribe user authentication
     */
    unsubscribeAuthentication: function () {
        if (this._isViafouraLoaded()) {
            window.vf.$unsubscribe("authentication", "required");
        }
    },

    /**
     * Fetch the current Viafoura notification context
     * @return {Promise<object>} The Viafoura notification context object
     */
    getNotificationContext: function () {
        if (this._isContextDefined()) {
            return window.vf.context.get("notifications");
        } else {
            return Promise.reject(
                "Viafoura context undefined - not loaded"
            ).catch((error) =>
                logger.warn(`(getNotificationContext) ${error}`)
            );
        }
    },

    /**
     * Determine if the Viafoura vf context has been initialized (window.vf.context)
     * @return {boolean} True if window.vf.context is defined, else false
     */
    _isContextDefined: function () {
        return this._isViafouraLoaded() && !!window.vf.context;
    },

    /**
     * Determine if the Viafoura vf object has been initialized (window.vf)
     * @return {boolean} True if window.vf is an object, else false
     */
    _isViafouraLoaded: function () {
        return isBrowser() && typeof window.vf === "object";
    },

    subscribeAuthenticationRequired: function (cb) {
        if (this._isViafouraLoaded()) {
            window.vf.$publish("tray", "close");
            window.vf.$subscribe("authentication", "required", () => cb());
        }
    },
};

export const getVfUrl = (url) => {
    const domainHost = getDomainHost();
    if (!domainHost || !url) {
        return null;
    }
    return url.includes("https://www.cbc.ca")
        ? url.replace("https://www.cbc.ca", `https://${domainHost}`)
        : url;
};

export default Viafoura;
